import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { useDatabase } from '../hooks/useDatabase';
import { usePublicForm } from '../hooks/usePublicContent';
import { submitLead } from '../services/leadService';
import { readOrigin } from '../utils/utm';
import { isValidUrl, postSubmitConfig, storeSubmission } from '../utils/postSubmit';
import { DynamicForm, DynamicFormResult } from '../components/public/DynamicForm';
import { Button } from '../components/ui/Button';
import { BrandLogo } from '../components/shared/BrandLogo';
import { normalizePublicSlug } from '../config/app';

/** Formulario público independiente: /formulario/:slug */
export function PublicForm() {
  const {
    slug: rawSlug = ''
  } = useParams();
  // Mismo saneado que en la landing pública: espacios, %20, barras y mayúsculas.
  const slug = normalizePublicSlug(rawSlug);
  const db = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    status,
    message,
    retry
  } = usePublicForm(slug);
  const form = db.forms.find(f => f.slug === slug);
  const organization = form ? db.organizations.find(o => o.id === form.organizationId) : undefined;
  const logoUrl = organization?.brand.logoUrl ?? organization?.logoUrl;
  if (status === 'cargando' && !form) {
    return <main className="flex min-h-screen w-full items-center justify-center bg-surface" aria-busy="true" data-id="element-2110" data-mp="dg8hhgh4rnmm">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-2111" data-mp="8hf4rq9o40ni" />
        <span className="sr-only" data-id="element-2112" data-mp="3q5hgwbld9es">Cargando el formulario…</span>
      </main>;
  }
  if (status === 'error' && !form) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-2113" data-mp="3uvoqtsq4kwl4">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" aria-hidden="true" data-id="element-2114" data-mp="w8ar47vcu3nd" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-2115" data-mp="rf7xjzqkuu76">
          No hemos podido cargar el formulario
        </h1>
        <p className="max-w-md text-sm text-muted-foreground" data-id="element-2116" data-mp="2lon2pc0615d7">
          Comprueba tu conexión e inténtalo de nuevo.
          {message ? ` (${message})` : ''}
        </p>
        <Button onClick={retry} data-id="element-2117" data-mp="2bx0iiq5unvyb">Reintentar</Button>
      </main>;
  }
  if (!form) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-2118" data-mp="2gwda1o7o1r78">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" data-id="element-2119" data-mp="22vsowlea3jak" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-2120" data-mp="26z7ea2o3xc0o">
          Formulario no disponible
        </h1>
        <Link to="/" data-id="element-2121" data-mp="1t07t54zszf99">
          <Button variant="secondary" data-id="element-2122" data-mp="1t0qzrpk06h7z">Volver al inicio</Button>
        </Link>
      </main>;
  }
  async function handleSubmit(result: DynamicFormResult) {
    if (!form) return;
    await submitLead({
      organizationId: form.organizationId,
      formId: form.id,
      contact: result.contact,
      answers: result.answers,
      origin: readOrigin(location.search, {
        formId: form.id
      })
    });
    // Envío de ESTE visitante, para personalizar la página de gracias.
    const thanksSlug = form.thankYouSlug ?? form.slug;
    storeSubmission(thanksSlug, {
      contact: result.contact,
      answers: result.answers
    });
    // El lead ya está guardado; ahora se ejecuta la acción configurada.
    const action = postSubmitConfig(form);
    if (action.action === 'url' && isValidUrl(action.redirectUrl)) {
      window.location.href = action.redirectUrl!.trim();
      return;
    }
    // WhatsApp y mensaje simple se resuelven dentro del propio formulario.
    if (action.action === 'whatsapp' || action.action === 'mensaje') return;
    navigate(`/gracias/${thanksSlug}`);
  }
  return <main className="min-h-screen w-full bg-surface px-5 py-16 sm:py-24" data-id="element-2123" data-mp="24farqu6h2ke7">
      <div className="mx-auto mb-12 max-w-2xl text-center" data-id="element-2124" data-mp="f0mjn54wzolz">
        <div className="mb-10 flex justify-center" data-id="element-2125" data-mp="a0rpmspueiwp">
          <BrandLogo src={logoUrl} size={30} data-id="element-2126" data-mp="a22ukvv0xwpe" />
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-2127" data-mp="2x9h2eanpsn9x">
          {form.name}
        </h1>
        {form.description && <p className="mt-4 text-base text-muted-foreground sm:text-lg" data-id="element-2128" data-mp="326h7ai8tvjzg">
            {form.description}
          </p>}
      </div>
      <DynamicForm form={form} onSubmit={handleSubmit} data-id="element-2129" data-mp="375anwjjevr3u" />
    </main>;
}